// Componentes de auth específicos do Clerk — só são montados quando
// clerkEnabled é true (ou seja, sob o ClerkProvider), então é seguro usar os
// hooks do Clerk aqui.
import { AuthenticateWithRedirectCallback, useAuth } from '@clerk/react';
import { type ReactElement, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ScreenLoading } from '@/components/loading';
import { ScreenLogin } from '@/components/screens/login-screen';
import { getClerk } from '@/store/session';
import { useScreenProps, useThemeStore } from '@/store/theme';

// Gate de rota: aguarda o Clerk carregar, redireciona p/ /login se não logado.
export function ClerkGate({ children }: Readonly<{ children: ReactElement }>) {
  const dark = useThemeStore((s) => s.dark);
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <ScreenLoading dark={dark} />;
  if (!isSignedIn) return <Navigate to="/login" replace />;
  return children;
}

// Login no modo Clerk: UM clique no botão dispara o OAuth da Microsoft direto
// (sem modal intermediário) → Azure AD → /sso-callback → /catalog.
export function ClerkLoginPage() {
  const sp = useScreenProps();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  useEffect(() => {
    if (isSignedIn) navigate('/catalog', { replace: true });
  }, [isSignedIn, navigate]);

  // UM clique → OAuth da Microsoft direto (sem modal). Usa o método canônico
  // do client global do Clerk (estável em runtime, sem o churn de tipos do hook).
  // O redirecionamento tira o usuário da página, então em caminho felizes nada
  // depende desta promessa. Mas ela PODE rejeitar (Clerk fora do ar, popup
  // bloqueado, estratégia desabilitada no painel) e aí o clique "não faz nada":
  // descartar com `void` apagaria a única pista. `.catch` com log deixa o motivo
  // no console de quem for investigar.
  const onSignIn = () => {
    getClerk()
      ?.client?.signIn?.authenticateWithRedirect({
        strategy: 'oauth_microsoft',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/catalog',
      })
      .catch((e: unknown) => {
        console.error('[auth] falha ao iniciar o SSO da Microsoft:', e);
      });
  };

  return (
    <div style={{ height: 'var(--app-vh)', overflow: 'hidden' }}>
      <ScreenLogin {...sp} onSignIn={onSignIn} />
    </div>
  );
}

// Página de retorno do OAuth: o Clerk finaliza o handshake e redireciona para
// o redirectUrlComplete (/catalog) definido no authenticateWithRedirect.
export function ClerkSsoCallback() {
  const dark = useThemeStore((s) => s.dark);
  return (
    <>
      <AuthenticateWithRedirectCallback />
      <ScreenLoading dark={dark} label="Concluindo login…" />
    </>
  );
}
